const hexToRgb = (hex: string): [number, number, number] => { // HEX 문자열을 [R, G, B] 배열로 변환
	hex = hex.replace(/^#/, ''); // # 제거
	if (hex.length === 3) {
		// 3자리 HEX(#abc) → 6자리 HEX(#aabbcc) 변환
		hex = hex.split('').map(x => x + x).join('');
	}
	const num = parseInt(hex, 16); // 16진수 → 10진수 변환
	// 비트 연산으로 각 색상값 추출
	return [
		(num >> 16) & 255, // Red
		(num >> 8) & 255,  // Green
		num & 255          // Blue
	];
};

const rgbToHex = ([r, g, b]: number[]) => { // [R, G, B] 배열을 HEX 문자열로 변환
	// 각 값을 16진수 2자리로 변환 후 연결
	return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

// [R, G, B] 배열을 [H, S, L] 배열로 변환
const rgbToHsl = ([r, g, b]: number[]) => {
	// 0~255 → 0~1로 정규화
	r /= 255; g /= 255; b /= 255;
	const max = Math.max(r, g, b), min = Math.min(r, g, b);
	let h = 0, s = 0, l = (max + min) / 2; // 밝기(L) 계산
	
	if (max !== min) { // 무채색이 아닐 때만 색상/채도 계산
		const d = max - min;
		// 채도(S) 계산
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		// 색상(H) 계산
		switch (max) {
			case r: h = (g - b) / d + (g < b ? 6 : 0); break;
			case g: h = (b - r) / d + 2; break;
			case b: h = (r - g) / d + 4; break;
		}
		h /= 6; // 0~1로 정규화
	}
	return [h * 360, s, l]; // H: 0~360, S/L: 0~1
};

const hslToRgb = ([h, s, l]: number[]) => { // [H, S, L] 배열을 [R, G, B] 배열로 변환
	h /= 360; // H를 0~1로 변환
	let r: number, g: number, b: number;
	
	if (s === 0) {
		// 채도가 0이면 무채색(회색)
		r = g = b = l;
	} else {
		// 색상 계산을 위한 보조 함수
		const hue2rgb = (p: number, q: number, t: number) => {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		};
		// 보조 변수 계산
		const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
		const p = 2 * l - q;
		// 각 색상값 계산
		r = hue2rgb(p, q, h + 1 / 3);
		g = hue2rgb(p, q, h);
		b = hue2rgb(p, q, h - 1 / 3);
	}
	// 0~1 → 0~255로 변환 후 정수화
	return [
		Math.round(r * 255),
		Math.round(g * 255),
		Math.round(b * 255)
	];
};

// 색상을 더 밝게 만드는 함수
export const lighten = (amount: number, hex: string) => {
	const rgb = hexToRgb(hex);        // HEX → RGB 변환
	let [h, s, l] = rgbToHsl(rgb);    // RGB → HSL 변환
	l = Math.min(1, l + amount);      // 밝기(L) 증가, 1(최대) 제한
	const newRgb = hslToRgb([h, s, l]); // HSL → RGB 변환
	return rgbToHex(newRgb);          // RGB → HEX 변환
};

// 색상을 더 어둡게 만드는 함수
export const darken = (amount: number, hex: string) => {
	const rgb = hexToRgb(hex);        // HEX → RGB 변환
	let [h, s, l] = rgbToHsl(rgb);    // RGB → HSL 변환
	l = Math.max(0, l - amount);      // 밝기(L) 감소, 0(최소) 제한
	const newRgb = hslToRgb([h, s, l]); // HSL → RGB 변환
	return rgbToHex(newRgb);          // RGB → HEX 변환
};
